// Shared cache for signed thumbnail URLs, keyed by the caller (public bank
// items use `pub:<filePermissionId>`, private ones `prv:<projectId>:<fileId>`).
// Modelled on scenes/ProjectGallery/utils/signedUrlCache.js: entries hold the
// fetch promise itself so concurrent callers share one in-flight request, and
// they expire because the presigned URLs do — the backend signs for 300 s, so
// the TTL is kept well under that.
export const THUMBNAIL_URL_TTL_MS = 4 * 60 * 1000;

/**
 * How long a presigned thumbnail URL is valid: the backend signs for 300 s.
 * The cache may hand a URL out up to THUMBNAIL_URL_TTL_MS after it was
 * signed, so a caller can only count on the difference.
 */
export const THUMBNAIL_URL_LIFETIME_MS = 5 * 60 * 1000;

const cache = new Map(); // key -> { promise, fetchedAt, url once resolved }

const isFresh = (entry) =>
  Boolean(entry) && Date.now() - entry.fetchedAt < THUMBNAIL_URL_TTL_MS;

/**
 * Returns a promise of the signed URL for `key`. A fresh entry (younger than
 * THUMBNAIL_URL_TTL_MS) returns the very same promise, so callers arriving
 * while a fetch is still in flight share it. A stale or missing entry calls
 * `fetcher()` again. A rejected fetch, a synchronous throw, or a resolved
 * falsy URL evicts the entry and rejects, so the next caller refetches.
 */
export function getCachedUrl(key, fetcher) {
  const hit = cache.get(key);
  if (isFresh(hit)) {
    return hit.promise;
  }

  let fetched;
  try {
    fetched = Promise.resolve(fetcher());
  } catch (error) {
    fetched = Promise.reject(error);
  }
  const entry = { fetchedAt: Date.now() };
  entry.promise = fetched.then((url) => {
    if (!url) {
      throw new Error(`thumbnailUrlCache: empty url for ${key}`);
    }
    // Kept on the entry, so invalidateUrl(key, url) can tell whether this
    // is the entry that handed that URL out.
    entry.url = url;
    return url;
  });

  cache.set(key, entry);
  entry.promise.catch(() => {
    // Only drop our own entry; the key may already hold a newer fetch.
    if (cache.get(key) === entry) {
      cache.delete(key);
    }
  });
  return entry.promise;
}

/**
 * True when `key` holds an entry younger than the TTL (resolved or in flight).
 */
export function hasFreshUrl(key) {
  return isFresh(cache.get(key));
}

/**
 * Drops the entry for `key`, e.g. after an <img> failed to load the URL.
 * Given the `url` that failed, only while the entry still holds that URL: a
 * newer URL for the key, resolved or still on its way, stays.
 * @param {string} key
 * @param {string} [url]
 */
export function invalidateUrl(key, url) {
  const entry = cache.get(key);
  if (!entry) return;
  if (url !== undefined && entry.url !== url) return;
  cache.delete(key);
}

/**
 * Empties the whole cache (tests).
 */
export function clearUrlCache() {
  cache.clear();
}
